import { SCHOOL_KEY_EXCEPTION } from "../../const/LocationConst";

/**
 * Global constant for valid grade tags used in Region Overview
 */
export const VALID_GRADE_TAGS = ["KINDERGARTEN", "FIRST", "SECOND", "THIRD", "FOURTH", "FIFTH", "SIXTH"];

const REGION_LOCATION_TYPES = ["ELEMENTARY_SCHOOL", "ELEMENTARY_LEGACY"];

const isEdcsd = (loc) => String(loc?.key) === String(SCHOOL_KEY_EXCEPTION);

/**
 * Build the set of schools the Region Overview may display.
 * Legacy (closed) elementary schools only belong to historical school years.
 *
 * @param {Object} params
 * @param {Array} [params.allLocationDtos] - Currently open elementary schools.
 * @param {Array} [params.legacyLocationDtos] - Retired elementary schools.
 * @param {boolean} [params.isHistorical] - True when a past school year is selected.
 * @returns {Array}
 */
export const buildRegionSchoolPool = ({ allLocationDtos, legacyLocationDtos, isHistorical } = {}) => {
    const active = allLocationDtos || [];
    if (!isHistorical) return [...active];

    const seen = new Set(active.map((loc) => String(loc.key)));
    const legacy = (legacyLocationDtos || []).filter((loc) => !seen.has(String(loc.key)));
    return [...active, ...legacy];
};

/**
 * Derive the sorted, unique region names selectable in the Region Overview dropdown.
 *
 * @param {Array} [pool] - Output of buildRegionSchoolPool.
 * @returns {Array<string>}
 */
export const deriveRegionOptions = (pool = []) =>
    [
        ...new Set(
            pool
                .filter((loc) => REGION_LOCATION_TYPES.includes(loc.locationType) && !isEdcsd(loc) && loc.region)
                .map((loc) => loc.region)
        )
    ].sort((a, b) => a.localeCompare(b));

/**
 * Select the schools belonging to a region. eDCSD and legacy schools with no region
 * are reported under every region.
 *
 * @param {Array} [pool] - Output of buildRegionSchoolPool.
 * @param {string} selectedRegion
 * @returns {Array}
 */
export const filterRegionLocations = (pool = [], selectedRegion) => {
    if (!selectedRegion) return [];
    return pool.filter((loc) => {
        if (isEdcsd(loc)) return true;
        if (!REGION_LOCATION_TYPES.includes(loc.locationType)) return false;
        if (loc.locationType === "ELEMENTARY_LEGACY" && !loc.region) return true;
        return String(loc.region).toLowerCase() === String(selectedRegion).toLowerCase();
    });
};

/**
 * Enriches and merges metrics data with location details and fallback values.
 *
 * @param {Array} regionLocations - The source list of locations for the region.
 * @param {Array} metricsArray - Metrics data from the EprOverview service.
 * @param {Array} locationsArray - Detailed location data from the Location service.
 * @returns {Array} Enriched metrics data for use in the UI.
 */
export const enrichRegionMetrics = (regionLocations, metricsArray, locationsArray) => {
    if (!regionLocations) return [];

    return regionLocations.map((loc) => {
        const metric = metricsArray.find((m) => String(m.locationKey) === String(loc.key));
        const locationDetail = locationsArray.find((l) => String(l.key) === String(loc.key));

        const locationGradeTags = (locationDetail?.tags || [])
            .filter((t) => t.type === "GRADE" && VALID_GRADE_TAGS.includes(t.tag))
            .map((t) => t.tag);

        // tags from metric first, then if none returned (like when new school year starts) use location tags
        const mergedTags = [...new Set([...(metric?.locationTags || []), ...locationGradeTags])];

        return {
            ...metric,
            locationKey: loc.key,
            locationName: metric?.locationName || loc.name,
            firstSemesterReleasedGrades: metric?.firstSemesterReleasedGrades || [],
            secondSemesterReleasedGrades: metric?.secondSemesterReleasedGrades || [],
            tags: mergedTags,
            firstSemesterTemplateCreated: metric?.firstSemesterTemplateCreated || false,
            secondSemesterTemplateCreated: metric?.secondSemesterTemplateCreated || false,
            slesUsed: metric?.slesUsed || 0,
            eprsReleasedRatio: metric?.eprsReleasedRatio || {},
            guardianAnalytics: metric?.guardianAnalytics || {}
        };
    });
};

/**
 * Keep schools that have a template for at least one semester.
 * @param {Array} [metrics]
 * @returns {Array}
 */
export const filterSchoolsWithAnyTemplate = (metrics = []) =>
    metrics.filter((m) => m?.firstSemesterTemplateCreated || m?.secondSemesterTemplateCreated);

/**
 * Comparator for region overview table rows.
 * @param {Object} a
 * @param {Object} b
 * @param {{ key: string, direction: string, semesterKey: string }} opts
 * @returns {number}
 */
export const compareMetrics = (a, b, { key, direction, semesterKey }) => {
    const dirMultiplier = direction === "ascending" ? 1 : -1;
    let aValue;
    let bValue;

    if (key === "locationName") {
        return (a.locationName || "").localeCompare(b.locationName || "") * dirMultiplier;
    }

    if (key === "guardianAnalytics" || key === "eprsReleasedRatio") {
        const aStr = a[key]?.[semesterKey] || "N/A";
        const bStr = b[key]?.[semesterKey] || "N/A";
        aValue = aStr === "N/A" ? -1 : Number.parseFloat(aStr.replace("%", ""));
        bValue = bStr === "N/A" ? -1 : Number.parseFloat(bStr.replace("%", ""));
    } else if (key === "templateCreated") {
        aValue = a[`${semesterKey}TemplateCreated`] ? 1 : 0;
        bValue = b[`${semesterKey}TemplateCreated`] ? 1 : 0;
    } else if (key === "slesUsed") {
        aValue = a[key] ? 1 : 0;
        bValue = b[key] ? 1 : 0;
    } else if (key === "releasedGrades") {
        aValue = (a[`${semesterKey}ReleasedGrades`] || []).length;
        bValue = (b[`${semesterKey}ReleasedGrades`] || []).length;
    } else {
        aValue = a[key];
        bValue = b[key];
    }

    if (aValue < bValue) return -1 * dirMultiplier;
    if (aValue > bValue) return 1 * dirMultiplier;
    return 0;
};
